import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Avatar, Spin } from 'antd';
import { GithubOutlined } from '@ant-design/icons';
import { ExpandDown } from '../../../../assets/CustomIcons/ExpandDown';
import { getUserProfilePictureUrl } from '../../../../common/userHelper';
import Workspace from '../../../../types/Workspace';
import { TaskStatus } from '../../../../types/TaskStatus';
import { GithubStatusRule, GithubStatusRuleEvent, GithubStatusRuleInput } from '../../../../types/Github';
import {
  useGetGithubInstallations,
  useGetGithubRepositories,
  useGetGithubStatusRules,
  useSetGithubStatusRulesMutation,
} from '../../../../serverState/github';
import { useGetUsersWithWorkspaceAccess, useGetWorkspaceById } from '../../../../serverState/workspace';
import { OrganizationAccessRoles } from '../../../../common/auth';
import { useOrgRoleInfo } from '../../../BaseIndex/useOrgRoleInfo';
import { BaseButton } from '../../../../components/UI/BaseButton';
import OwnerDropdown from '../../../../components/UI/Dropdown/OwnerDropdown';
import { User } from '../../../../types/User';
import CustomTooltip from '../../../../components/UI/Tooltip/CustomTooltip';
import { GithubStatusRuleSelect, ruleToValue, valueToRuleInput } from './GithubStatusRuleSelect';
import { SpaceSettingsGithubRepositories } from './SpaceSettingsGithubRepositories';

/**
 * Maps each pull request event to the status the task should move to.
 *
 * Lives in Space Settings rather than the organization Integrations page because the
 * rules are per-space, the options are that space's own statuses, and the write is
 * `spaceGuard(OWNER)` - the same permission the modal already reflects in `isDisabled`.
 * Reading is MEMBER, so a non-owner sees the configuration read-only.
 */
export type SpaceSettingsGithubProps = {
  space?: Workspace;
  setIsChanged?: (isChanged: boolean) => void;
  isDisabled?: boolean;
};

const EVENT_COPY: Record<GithubStatusRuleEvent, { title: string; description: string }> = {
  pr_opened: {
    title: 'Opened',
    description: 'A pull request that links to the task is opened, or marked ready for review',
  },
  pr_merged: {
    title: 'Merged',
    description: 'Every pull request linked to the task has been merged or closed',
  },
  pr_closed: {
    title: 'Closed',
    description: 'A pull request is closed without being merged',
  },
  pr_reopened: {
    title: 'Reopened',
    description: 'A closed pull request is reopened',
  },
};

/** Same ordering the rest of the app lists statuses in (see `StatusDropdown`). */
const sortStatuses = (statuses: TaskStatus[]): TaskStatus[] =>
  [...statuses].sort((a, b) => (a.orderIndex ?? a.id) - (b.orderIndex ?? b.id));

export const SpaceSettingsGithub = ({ space, setIsChanged, isDisabled }: SpaceSettingsGithubProps) => {
  const spaceId = space?.id ?? null;
  const { activeOrganization, organizationRole } = useOrgRoleInfo();
  const isOrgAdmin = organizationRole === OrganizationAccessRoles.ADMIN;

  const rulesQuery = useGetGithubStatusRules(spaceId);
  const workspaceQuery = useGetWorkspaceById(spaceId);
  const repositoriesQuery = useGetGithubRepositories(activeOrganization?.id ?? null);
  // Both this and the repository list are organizationGuard(MEMBER), so a space owner
  // who is not an organization admin can still read enough to be told WHICH thing is
  // missing - the whole point of the states below. Only `mappable-workspaces` and the
  // writes are ADMIN, and this tab calls neither.
  const installationsQuery = useGetGithubInstallations(activeOrganization?.id ?? null);
  const membersQuery = useGetUsersWithWorkspaceAccess(spaceId);
  const saveMutation = useSetGithubStatusRulesMutation();

  const [values, setValues] = useState<Record<string, string> | null>(null);
  const [assignees, setAssignees] = useState<Record<string, number | null> | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Two independent saves now live on this tab - the rules table below, and the
  // repository picker above it, which writes to a different endpoint - but the modal has
  // one `isChanged` flag guarding tab switches and close. Both halves have to be able to
  // raise it without clearing the other's, so each reports through a ref the other reads.
  //
  // Refs rather than state, and read only from handlers (never during render): making
  // either half's dirtiness a dependency of the other's would mean rerendering the rules
  // table to tick a checkbox.
  const reposDirtyRef = useRef(false);
  const rulesDirtyRef = useRef(false);

  const rules = rulesQuery.data;

  // Reseed whenever the server's view changes - on first load, and after a save, where
  // the mutation writes the freshly-resolved list straight into the cache.
  useEffect(() => {
    if (!rules) return;
    setValues(Object.fromEntries(rules.map((rule) => [rule.event, ruleToValue(rule)])));
    setAssignees(Object.fromEntries(rules.map((rule) => [rule.event, rule.assigneeId])));
  }, [rules]);

  const dirty = (nextValues: Record<string, string>, nextAssignees: Record<string, number | null>): boolean =>
    !!rules &&
    rules.some((rule) => nextValues[rule.event] !== ruleToValue(rule) || nextAssignees[rule.event] !== rule.assigneeId);

  const isChanged = !!values && !!assignees && dirty(values, assignees);

  useEffect(() => {
    rulesDirtyRef.current = isChanged;
  }, [isChanged]);

  // Stable, because it is a dependency of the picker's own effect: rebuilding it every
  // render would make that effect re-run every render, and its cleanup reports `false`.
  const onReposDirtyChange = useCallback(
    (reposDirty: boolean) => {
      reposDirtyRef.current = reposDirty;
      setIsChanged?.(reposDirty || rulesDirtyRef.current);
    },
    [setIsChanged]
  );

  const onSelect = (event: GithubStatusRuleEvent, value: string) => {
    if (!rules) return;
    const next = { ...(values ?? {}), [event]: value };
    setValues(next);
    setIsChanged?.(dirty(next, assignees ?? {}) || reposDirtyRef.current);
  };

  const onAssign = (event: GithubStatusRuleEvent, assigneeId: number | null) => {
    if (!rules) return;
    const next = { ...(assignees ?? {}), [event]: assigneeId };
    setAssignees(next);
    setIsChanged?.(dirty(values ?? {}, next) || reposDirtyRef.current);
  };

  const onSave = async () => {
    if (!spaceId || !rules || !values || !assignees) return;
    setError(null);
    const payload: GithubStatusRuleInput[] = rules.map((rule) =>
      valueToRuleInput(rule.event, values[rule.event], assignees[rule.event] ?? null)
    );
    try {
      await saveMutation.mutateAsync({ spaceId, rules: payload });
      // Only the rules were saved; an unsaved repository selection still counts.
      setIsChanged?.(reposDirtyRef.current);
    } catch {
      setError('Could not save these rules. Please try again.');
    }
  };

  const heading = <div className="text-brand-grey-dark text-base font-semibold">GitHub</div>;

  if (rulesQuery.isLoading || workspaceQuery.isLoading) {
    return (
      <div className="w-full flex items-center justify-center py-20">
        <Spin />
      </div>
    );
  }

  if (rulesQuery.isError || !rules) {
    return (
      <div className="w-full h-full px-8 pt-6 flex flex-col items-start">
        {heading}
        <p className="m-0 mt-2 text-sm text-neutral-900">
          Could not load the GitHub rules for this space. Please try again.
        </p>
        <BaseButton type="secondary" size="medium" onClick={() => void rulesQuery.refetch()}>
          Retry
        </BaseButton>
      </div>
    );
  }

  const statuses = sortStatuses((workspaceQuery.data?.TaskStatus as TaskStatus[]) ?? []);
  // The space's own members, which is exactly the set the server will accept: the rules
  // PUT rejects an assignee who has no access to this space, and so does the transition
  // when the rule fires.
  const members: User[] = (membersQuery.data ?? []).map((access) => access.User);
  const mappedRepositories = (repositoriesQuery.data ?? []).filter((repository) => repository.workspaceId === spaceId);

  // Only claim "not connected" once the query has actually settled. On an error we do
  // not know, and falling through to the rules table is the better failure of the two:
  // the rules are readable and savable whatever the organization has installed, whereas
  // an empty state told to someone whose organization IS connected is simply wrong.
  const notConnected =
    !installationsQuery.isLoading && !installationsQuery.isError && (installationsQuery.data ?? []).length === 0;

  // Two different missing things, and a space owner who is not an organization admin can
  // fix NEITHER of them from here - Settings → Integrations is admin-gated, and so is the
  // repository mapping write. Naming what is missing and who can do it is the whole
  // content of this state; sending a non-admin to a page they cannot open is not.
  if (notConnected) {
    return (
      <div className="w-full h-full px-8 pt-6 flex flex-col items-start">
        {heading}
        <div className="mt-4 box-border w-full rounded-lg bg-brand-grey-light p-6 flex flex-col items-center text-center">
          <GithubOutlined className="text-2xl text-neutral-900" />
          <div className="mt-3 text-sm font-medium text-brand-grey-dark">GitHub is not connected</div>
          <p className="m-0 mt-1 max-w-[420px] text-sm leading-relaxed text-neutral-900">
            Connect GitHub for this organization and map a repository to this space, and pull requests will be able to
            move its tasks automatically.
          </p>
          <p className="m-0 mt-3 text-sm leading-relaxed text-neutral-900">
            {isOrgAdmin ? 'Connect it in Settings → Integrations.' : 'Ask an organization admin to connect it.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full px-8 flex flex-col items-start justify-between">
      <div className="w-full flex flex-col items-start mt-6">
        {heading}

        <p className="m-0 mt-2 text-sm leading-relaxed text-neutral-900">
          Move a task automatically when a pull request that links to it changes state. Every event starts at{' '}
          <span className="font-medium">No change</span>. A merge can also hand the task to someone, with or without a
          status change.
        </p>

        {/* Which repositories, before how their pull requests behave: the rules below do
            nothing at all until one is connected. */}
        <SpaceSettingsGithubRepositories
          spaceId={spaceId}
          organizationId={activeOrganization?.id ?? null}
          isDisabled={isDisabled}
          mappedRepositories={mappedRepositories}
          onDirtyChange={onReposDirtyChange}
        />

        {/* Only worth saying while the picker above shows nothing connected. A space owner
            can now fix it in place, so this no longer sends anybody to an admin - it only
            explains why the table below would do nothing. */}
        {mappedRepositories.length === 0 && !repositoriesQuery.isLoading && (
          <p className="m-0 mt-4 w-full rounded-lg bg-brand-grey-light p-3 text-sm leading-relaxed text-neutral-900">
            No GitHub repository is connected to this space yet, so nothing will move.{' '}
            {isDisabled ? 'Only a space owner can connect one.' : 'Connect one above and these rules start applying.'}
          </p>
        )}

        {error && <Alert className="mt-4 w-full" type="error" showIcon={true} message={error} />}

        <div className="mt-5 w-full flex items-center justify-between gap-3 border-0 border-b border-solid border-neutral-300 pb-2">
          <span className="min-w-0 flex-1 text-xs font-medium uppercase tracking-wide text-neutral-900">
            When a pull request is
          </span>
          <span className="w-48 shrink-0 text-xs font-medium uppercase tracking-wide text-neutral-900">
            Move the task to
          </span>
          <span className="w-36 shrink-0 text-xs font-medium uppercase tracking-wide text-neutral-900">Assign to</span>
        </div>

        <ul className="m-0 w-full list-none p-0">
          {rules.map((rule: GithubStatusRule) => {
            const statusValue = values?.[rule.event] ?? ruleToValue(rule);
            const assigneeId = assignees?.[rule.event] ?? rule.assigneeId;
            const assignee = members.find((member) => member.id === assigneeId) ?? null;
            return (
              <li
                key={rule.event}
                className="flex items-center justify-between gap-3 border-0 border-b border-solid border-neutral-300 py-3 last:border-b-0"
              >
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="text-sm font-medium text-brand-grey-dark">{EVENT_COPY[rule.event].title}</span>
                  <span className="text-xs leading-snug text-neutral-900">{EVENT_COPY[rule.event].description}</span>
                </div>
                <div className="w-48 shrink-0">
                  <GithubStatusRuleSelect
                    rule={rule}
                    statuses={statuses}
                    value={statusValue}
                    disabled={isDisabled}
                    onChange={(value) => onSelect(rule.event, value)}
                  />
                </div>
                {/* Merged only. The handover this exists for is "the work is finished,
                    give it to a tester", and the server rejects an assignee on any other
                    event - so offering one here would build a control whose save 400s.
                    The other rows keep the cell so the columns stay aligned. */}
                <div className="w-36 shrink-0">
                  {rule.event === 'pr_merged' && (
                    <OwnerDropdown
                      owner={assignee}
                      owners={members}
                      size="medium"
                      hideOutline={true}
                      // Live even at "No change": an owner applies on its own, without a
                      // status move.
                      disabled={isDisabled}
                      removeLabel="Don't change"
                      tooltipTitle={assignee ? '' : 'Leave the assignee alone'}
                      onSelect={(member) => onAssign(rule.event, member?.id ?? null)}
                      // `selectedElement` covers BOTH states rather than pairing `owner`
                      // with `emptyElement`, so the chevron is present either way: this
                      // control sits beside a status picker that has one, and without it
                      // the cell reads as a label rather than something you can change.
                      selectedElement={
                        <div className="flex min-w-0 items-center gap-1">
                          {assignee ? (
                            <>
                              <Avatar
                                size={20}
                                src={getUserProfilePictureUrl(assignee.profilePicturePath)}
                                className="flex shrink-0 items-center justify-center bg-neutral-200"
                              >
                                <span className="text-center text-[10px] font-semibold leading-3 text-slate-500">
                                  {(assignee.fullName || assignee.email)?.slice(0, 1)}
                                </span>
                              </Avatar>
                              <span className="truncate text-sm font-normal leading-tight text-neutral-900">
                                {assignee.fullName || assignee.email}
                              </span>
                            </>
                          ) : (
                            <span className="truncate text-sm font-normal leading-tight text-neutral-900">
                              Don&apos;t change
                            </span>
                          )}
                          <ExpandDown className="shrink-0 text-neutral-900" />
                        </div>
                      }
                    />
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {isDisabled && (
        <CustomTooltip title={'Only a space owner can change these rules'}>
          <div className="absolute top-[80px] left-[250px] w-[calc(100%-250px)] h-[calc(100%-80px)] bg-white/30 z-10 cursor-not-allowed pointer-events-auto" />
        </CustomTooltip>
      )}

      <div className="w-full flex justify-end items-center mb-3">
        <BaseButton
          type="primary"
          size="medium"
          disabled={!isChanged || saveMutation.isPending}
          onClick={() => void onSave()}
        >
          Save Changes
        </BaseButton>
      </div>
    </div>
  );
};
